import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import BottomNav from "./components/BottomNav";
import AuthPage from "./pages/AuthPage";
import ClubGate from "./pages/ClubGate";
import ClubSelectionPage from "./pages/ClubSelectionPage";
import ClubCreationPage from "./pages/ClubCreationPage";
import ClubSettingsPage from "./pages/ClubSettingsPage";
import PlayerSkillsPage from "./pages/PlayerSkillsPage";
import HomePage from "./pages/HomePage";
import MembersPage from "./pages/MembersPage";
import MemberDetailPage from "./pages/MemberDetailPage";
import MatchesPage from "./pages/MatchesPage";
import MatchDetailPage from "./pages/MatchDetailPage";
import ExpenseNewPage from "./pages/ExpenseNewPage";
import LedgerPage from "./pages/LedgerPage";
import MyMatchesPage from "./pages/MyMatchesPage";
import StorePage from "./pages/StorePage";
import ProfilePage from "./pages/ProfilePage";
import PollsPage from "./pages/PollsPage";
import PollNewPage from "./pages/PollNewPage";
import PollDetailPage from "./pages/PollDetailPage";
import PricingPage from "./pages/PricingPage";
import PlanDetailsPage from "./pages/PlanDetailsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import { AdminClubsPage, AdminClubDetailPage } from "./pages/AdminClubsPage";
import InvitationsPage from "./pages/InvitationsPage";
import ScoreNewPage from "./pages/ScoreNewPage";
import ScoreLivePage from "./pages/ScoreLivePage";
import PollReportPage from "./pages/PollReportPage";
import PublicDayReportPage from "./pages/PublicDayReportPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import InstallPrompt from "./components/InstallPrompt";

function LoadingScreen() {
  return (
    <div className="app-shell grid place-items-center">
      <div className="text-center">
        <img src="/logo.png" alt="Cricso" className="h-16 w-16 object-contain mx-auto" />
        <div className="mt-3 text-sm text-ink-muted">Loading…</div>
      </div>
    </div>
  );
}

function Protected({ children, allowAdmin = false }) {
  const { user, club, clubs } = useAuth();
  const loc = useLocation();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  // Allow admin access even without active club
  if (allowAdmin && user.is_superadmin) return children;
  // Allow club creation even without active club
  if (loc.pathname === "/clubs/create") return children;
  // If user has multiple clubs but no active club, show club selection
  if (clubs.length > 1 && !user.active_club_id) return <Navigate to="/clubs" replace />;
  // If user has clubs but no active club, redirect to setup
  if (clubs.length > 0 && !user.active_club_id) return <Navigate to="/setup" replace />;
  // If no clubs at all, redirect to setup
  if (!user.active_club_id && clubs.length === 0) return <Navigate to="/setup" replace />;
  if (!club) return <LoadingScreen />;
  // Redirect to skills setup if they haven't set skills yet and not already on skills page
  // Only redirect if explicitly false, not if undefined (for backwards compatibility)
  if (user.skills_set === false && loc.pathname !== "/skills") return <Navigate to="/skills" replace />;
  return children;
}

// Pages only club owners/admins/managers can open; members are sent home
function ClubAdminOnly({ children }) {
  const { club } = useAuth();
  if (!["owner", "admin", "manager"].includes(club?.my_role)) return <Navigate to="/home" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user) return <Navigate to="/home" replace />;
  return children;
}

function AppShell({ children }) {
  return (
    <div className="app-shell">
      <div className="min-h-screen">
        {children}
      </div>
      <BottomNav />
    </div>
  );
}

export default function App() {
  return (
    <>
      <InstallPrompt />
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
        <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />

        <Route path="/setup" element={
          <SetupGuard><ClubGate /></SetupGuard>
        } />

        <Route path="/clubs" element={<ClubSelectionPage />} />
        <Route path="/clubs/create" element={<Protected><ClubCreationPage /></Protected>} />
        <Route path="/clubs/:clubId/settings" element={<Protected><AppShell><ClubSettingsPage /></AppShell></Protected>} />

        <Route path="/skills" element={
          <Protected><PlayerSkillsPage /></Protected>
        } />

        <Route path="/home" element={<Protected><AppShell><HomePage /></AppShell></Protected>} />
        <Route path="/members" element={<Protected><AppShell><MembersPage /></AppShell></Protected>} />
        <Route path="/members/:id" element={<Protected><AppShell><MemberDetailPage /></AppShell></Protected>} />
        <Route path="/matches" element={<Protected><AppShell><MatchesPage /></AppShell></Protected>} />
        <Route path="/matches/:id" element={<Protected><AppShell><MatchDetailPage /></AppShell></Protected>} />
        <Route path="/scores/new" element={<Protected><AppShell><ScoreNewPage /></AppShell></Protected>} />
        <Route path="/scores/:id" element={<Protected><AppShell><ScoreLivePage /></AppShell></Protected>} />
        <Route path="/polls" element={<Protected><AppShell><PollsPage /></AppShell></Protected>} />
        <Route path="/polls/new" element={<Protected><AppShell><PollNewPage /></AppShell></Protected>} />
        <Route path="/polls/:id/edit" element={<Protected><AppShell><PollNewPage /></AppShell></Protected>} />
        <Route path="/polls/:id/report" element={<Protected><AppShell><PollReportPage /></AppShell></Protected>} />
        <Route path="/polls/:id" element={<Protected><AppShell><PollDetailPage /></AppShell></Protected>} />
        <Route path="/expenses/new" element={<Protected><AppShell><ExpenseNewPage /></AppShell></Protected>} />
        <Route path="/ledger" element={<Protected><ClubAdminOnly><AppShell><LedgerPage /></AppShell></ClubAdminOnly></Protected>} />
        <Route path="/stats" element={<Protected><AppShell><MyMatchesPage /></AppShell></Protected>} />
        <Route path="/my-matches" element={<Navigate to="/stats" replace />} />
        <Route path="/store" element={<Protected><AppShell><StorePage /></AppShell></Protected>} />
        <Route path="/profile" element={<Protected><AppShell><ProfilePage /></AppShell></Protected>} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/share/day/:id" element={<PublicDayReportPage />} />
        <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/pricing/:planId" element={<Protected><AppShell><PlanDetailsPage /></AppShell></Protected>} />
        <Route path="/subscription" element={<Protected><AppShell><SubscriptionPage /></AppShell></Protected>} />
        <Route path="/invitations" element={<Protected><AppShell><InvitationsPage /></AppShell></Protected>} />
        <Route path="/admin" element={<Protected allowAdmin><AppShell><AdminDashboardPage /></AppShell></Protected>} />
        <Route path="/admin/clubs" element={<Protected allowAdmin><AppShell><AdminClubsPage /></AppShell></Protected>} />
        <Route path="/admin/clubs/:id" element={<Protected allowAdmin><AppShell><AdminClubDetailPage /></AppShell></Protected>} />

        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    </>
  );
}

function SetupGuard({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}
